/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Mutable state for the forecast
 * pages: approvals, growth factors, pinned months, corrected past months,
 * outlier rule switches and an audit trail. Resets on page reload or through `resetForecastDemo()`.
 */
import { useMemo } from 'react';
import { createDemoStore } from '@/demo/core/create-demo-store';
import { getPerson, PERSONA_ID } from '@/demo/procurement/data/company';
import { AXIS, getSpec, rawHistoryFor } from './forecast-data';
import {
  HISTORY_REASON_LABEL,
  buildViews,
  formatFactorPercent,
  seedForecastState,
} from './forecast-engine';
import { fmtUnits } from './forecast-format';
import type {
  ApprovalStatus,
  ForecastDemoState,
  ForecastView,
  HistoryEdit,
} from './forecast-types';

const store = createDemoStore<ForecastDemoState>(seedForecastState);

export function useForecastState(): ForecastDemoState {
  return store.useStore((state) => state);
}

export function getForecastState(): ForecastDemoState {
  return store.getState();
}

/** Every series as the pages read it, recomputed when a buyer changes anything. */
export function useForecastViews(): ForecastView[] {
  const state = useForecastState();
  return useMemo(() => buildViews(state), [state]);
}

function actor(): string {
  return getPerson(PERSONA_ID).name;
}

let auditCounter = 0;

function logChange(
  draft: ForecastDemoState,
  seriesId: string,
  text: string,
): void {
  auditCounter += 1;
  draft.audit = [
    {
      id: `audit-${auditCounter}`,
      seriesId,
      at: new Date().toISOString(),
      by: actor(),
      text,
    },
    ...draft.audit,
  ];
}

/**
 * Any change a buyer makes to the forecast itself sends an approved forecast
 * back for sign-off. Corrections to past months do not: they fix the data the
 * model reads, not the plan.
 */
function sendForApproval(
  draft: ForecastDemoState,
  seriesId: string,
  what: string,
): void {
  if (draft.approvals[seriesId]?.status !== 'approved') return;
  draft.approvals[seriesId] = {
    status: 'needs_approval',
    note: `${what}. Needs sign-off before stock levels follow it.`,
    decidedBy: null,
    decidedAt: null,
  };
}

export function decideApproval(
  seriesId: string,
  status: Exclude<ApprovalStatus, 'needs_approval'>,
  reason: string,
): void {
  store.update((draft) => {
    const previous = draft.approvals[seriesId];
    draft.approvals[seriesId] = {
      status,
      note: reason.trim() || previous?.note || '',
      decidedBy: actor(),
      decidedAt: new Date().toISOString(),
    };
    logChange(
      draft,
      seriesId,
      `${status === 'approved' ? 'Approved' : 'Rejected'} the forecast${reason.trim() ? `: ${reason.trim()}` : ''}`,
    );
  });
}

/** Puts a decision back in the queue. */
export function reopenApproval(seriesId: string): void {
  store.update((draft) => {
    const previous = draft.approvals[seriesId];
    draft.approvals[seriesId] = {
      status: 'needs_approval',
      note: previous?.note ?? '',
      decidedBy: null,
      decidedAt: null,
    };
    logChange(draft, seriesId, 'Reopened the forecast for approval');
  });
}

export function setGrowthFactor(
  seriesId: string,
  factor: number | null,
  fromIndex: number,
  reason: string,
  log = true,
  toIndex?: number,
): void {
  store.update((draft) => {
    if (factor === null || factor === 1) {
      delete draft.factors[seriesId];
      if (log) logChange(draft, seriesId, 'Removed the growth adjustment');
      return;
    }
    draft.factors[seriesId] = {
      factor,
      fromIndex,
      ...(toIndex === undefined ? {} : { toIndex }),
      reason: reason.trim() || 'No reason given',
    };
    sendForApproval(
      draft,
      seriesId,
      `A growth adjustment of ${formatFactorPercent(factor)} was added`,
    );
    if (log) {
      logChange(
        draft,
        seriesId,
        `Set a growth adjustment of ${formatFactorPercent(factor)} from ${AXIS.forecast[fromIndex]?.label ?? 'this month'}${toIndex === undefined ? '' : ` through ${AXIS.forecast[toIndex - 1]?.label ?? 'the end of the forecast'}`}`,
      );
    }
  });
}

export function setPinnedMonth(
  seriesId: string,
  monthKey: string,
  value: number | null,
  log = true,
): void {
  store.update((draft) => {
    const pins = { ...(draft.pins[seriesId] ?? {}) };
    if (value === null) {
      delete pins[monthKey];
    } else {
      pins[monthKey] = Math.max(0, Math.round(value));
    }
    draft.pins[seriesId] = pins;
    const month =
      AXIS.forecast.find((candidate) => candidate.key === monthKey)?.label ??
      monthKey;
    const unit = getSpec(seriesId)?.unit ?? 'each';
    if (value !== null) {
      sendForApproval(draft, seriesId, `${month} was set by a buyer`);
    }
    if (log) {
      logChange(
        draft,
        seriesId,
        value === null
          ? `Cleared the fixed value for ${month}`
          : `Fixed ${month} at ${fmtUnits(Math.max(0, Math.round(value)), unit)}`,
      );
    }
  });
}

export interface UpliftRequest {
  seriesIds: string[];
  /** 30 reads as +30%, -10 as -10%. */
  percent: number;
  /** First forecast month index and the one it stops before. */
  fromIndex: number;
  toIndex: number;
  reason: string;
  /** Who asked, for the change log. */
  via: string;
}

/**
 * A percent change over a window of forecast months, the way a buyer would ask
 * for it: "we will sell 30% more over the next 6 months". Fixed months inside
 * the window are cleared so the change is not masked, and the forecast goes
 * back for approval.
 */
export function applyUplift(request: UpliftRequest): void {
  const { percent, fromIndex, toIndex } = request;
  const factor = 1 + percent / 100;
  const first = AXIS.forecast[fromIndex]?.label ?? 'this month';
  const last = AXIS.forecast[toIndex - 1]?.label ?? 'the end of the forecast';
  store.update((draft) => {
    for (const seriesId of request.seriesIds) {
      draft.factors[seriesId] = {
        factor,
        fromIndex,
        toIndex,
        reason: request.reason,
      };
      const pins = { ...(draft.pins[seriesId] ?? {}) };
      AXIS.forecast.slice(fromIndex, toIndex).forEach((month) => {
        delete pins[month.key];
      });
      draft.pins[seriesId] = pins;
      sendForApproval(
        draft,
        seriesId,
        `The forecast was changed ${formatFactorPercent(factor)} for ${first} to ${last}`,
      );
      logChange(
        draft,
        seriesId,
        `Changed the forecast ${formatFactorPercent(factor)} for ${first} to ${last}, asked through ${request.via}: ${request.reason}`,
      );
    }
  });
}

/** Takes every buyer change off the forecast of these series (growth change and fixed months). */
export function clearForecastChanges(
  seriesIds: readonly string[],
  text: string,
): void {
  store.update((draft) => {
    for (const seriesId of seriesIds) {
      const hadChange =
        draft.factors[seriesId] !== undefined ||
        Object.keys(draft.pins[seriesId] ?? {}).length > 0;
      delete draft.factors[seriesId];
      delete draft.pins[seriesId];
      if (hadChange) logChange(draft, seriesId, text);
    }
  });
}

/** Records one entry in the change log, for edits that apply a value live. */
export function recordChange(seriesId: string, text: string): void {
  store.update((draft) => {
    logChange(draft, seriesId, text);
  });
}

/**
 * Corrects (or restores, with null) one past month. Leaving a spike out is the
 * same call with the typical month as the value, so every correction reads,
 * undoes and logs the same way.
 */
export function setHistoryEdit(
  seriesId: string,
  monthKey: string,
  edit: HistoryEdit | null,
  text?: string,
): void {
  const spec = getSpec(seriesId);
  const index = AXIS.history.findIndex((month) => month.key === monthKey);
  const month = AXIS.history[index];
  if (!spec || !month) return;
  const original = rawHistoryFor(spec)[index] ?? 0;
  store.update((draft) => {
    const edits = { ...(draft.historyEdits[seriesId] ?? {}) };
    if (edit === null || Math.round(edit.value) === original) {
      delete edits[monthKey];
      draft.historyEdits[seriesId] = edits;
      logChange(
        draft,
        seriesId,
        text ??
          `Counted ${month.label} as recorded again: ${fmtUnits(original, spec.unit)}`,
      );
      return;
    }
    const value = Math.max(0, Math.round(edit.value));
    edits[monthKey] = { ...edit, value };
    draft.historyEdits[seriesId] = edits;
    logChange(
      draft,
      seriesId,
      text ??
        `Corrected ${month.label} from ${fmtUnits(original, spec.unit)} to ${fmtUnits(value, spec.unit)}: ${HISTORY_REASON_LABEL[edit.reason].toLowerCase()}${edit.note ? ` (${edit.note})` : ''}`,
    );
  });
}

/** Puts every corrected past month on the series back to what was recorded. */
export function clearHistoryEdits(seriesId: string): void {
  store.update((draft) => {
    if (Object.keys(draft.historyEdits[seriesId] ?? {}).length === 0) return;
    delete draft.historyEdits[seriesId];
    logChange(draft, seriesId, 'Reset every change to past months');
  });
}

/** Replaces every fixed month on the series, for "follow the panel build plan". */
export function replacePins(
  seriesId: string,
  pins: Record<string, number>,
  text: string,
): void {
  store.update((draft) => {
    draft.pins[seriesId] = { ...pins };
    sendForApproval(draft, seriesId, text);
    logChange(draft, seriesId, text);
  });
}

export function clearPins(seriesId: string): void {
  store.update((draft) => {
    delete draft.pins[seriesId];
    logChange(draft, seriesId, 'Cleared every fixed month');
  });
}

export function setRuleEnabled(ruleId: string, enabled: boolean): void {
  store.update((draft) => {
    draft.ruleEnabled[ruleId] = enabled;
  });
}

export function resetForecastDemo(): void {
  store.reset();
}
